import { UniqueIdentifier } from "@dnd-kit/core";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { DEFAULT_CATALOG_VIEW } from "@redotech/redo-inventory-common/views/default-catalog-products-views";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import { CatalogProductsViewsContext } from "@redotech/redo-merchant-inventory-app/inventory-views";
import { SortableTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/sortable-tree";
import { TreeItem } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/types";
import { flattenTree } from "@redotech/redo-web/arbiter-components/navigation/folder-tree/utilities";
import { RedoSecondaryNavItem } from "@redotech/redo-web/arbiter-components/navigation/secondary/redo-secondary-nav-item";
import { memo, useMemo } from "react";
import { useMatch } from "react-router-dom";
import {
  FolderWithView,
  treeToViewsAndFolders,
  viewsAndFoldersToTree,
} from "../orders/view-to-tree-utils";

export const AeoCatalogProductsItem = memo(function AeoCatalogProductsItem() {
  const routes = useRequiredContext(MerchantRoutesContext);
  const {
    views,
    folders,
    openEditFolderModal,
    openCreateFolderModal,
    updateOrdering,
  } = useRequiredContext(CatalogProductsViewsContext);
  const urlPrefix = `${routes.aeoCatalogHref}/products`;

  const viewsWithoutFolders = useMemo(() => {
    return views?.filter((view) => !view.folderId) || [];
  }, [views]);

  const foldersWithViews = useMemo<FolderWithView<"catalog-products">[]>(() => {
    return (
      folders?.map((folder) => {
        return {
          folder,
          views: views?.filter((view) => view.folderId === folder._id) || [],
        };
      }) || []
    );
  }, [folders, views]);

  const sortableItems = useMemo(() => {
    return viewsAndFoldersToTree({
      viewsWithoutFolders,
      foldersWithViews,
      urlPrefix,
      viewCounts: {},
      folderCounts: {},
      hideZeroNotifications: false,
    });
  }, [viewsWithoutFolders, foldersWithViews, urlPrefix]);

  const handleRequestEdit = useHandler((id: UniqueIdentifier) => {
    openEditFolderModal(id.toString());
  });

  const handleUpdateOrdering = useHandler((items: TreeItem[]) => {
    const flattenedItems = flattenTree(items);
    const result = treeToViewsAndFolders<"catalog-products">(
      flattenedItems,
      views || [],
      folders || [],
    );
    updateOrdering(result.views, result.folders);
  });

  const anyChildSelected = useMatch(`${urlPrefix}/*`);

  return (
    <RedoSecondaryNavItem
      action={{ type: "link", href: `${urlPrefix}/${DEFAULT_CATALOG_VIEW}` }}
      anyChildSelected={!!anyChildSelected}
      friendlyName="Products"
      id="aeo-catalog-products"
    >
      <SortableTree
        items={sortableItems}
        onRequestEdit={handleRequestEdit}
        setItems={handleUpdateOrdering}
      />
      <RedoSecondaryNavItem
        action={{
          type: "button",
          onClick: () => {
            openCreateFolderModal();
          },
        }}
        friendlyName="+ Add new folder"
        id="aeo-catalog-products-create-folder"
      />
    </RedoSecondaryNavItem>
  );
});
